import AddContactsDialog from "@/components/AddContactsDialog";
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, Users, User, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import type CampaignContact from "@/model/interface/CampaignContact";
import type { CampaignContactStatus } from "@/model/interface/CampaignContact";
import type { SequenceEnrollment } from "@/model/interface/Sequence";
import { useGetCampaignEnrollmentsQuery, useRemoveCampaignContactsMutation } from "@/store/rest_api";
import { getInitials, getContactDisplayName } from "@/util/string";

interface CampaignContactsSectionProps {
  campaignId: string;
  contacts: CampaignContact[];
  isLoading: boolean;
  campaignType?: string;
}

const statusVariants: Record<CampaignContactStatus, "default" | "secondary" | "outline" | "destructive"> = {
  QUEUED: "secondary",
  DIALING: "default",
  RINGING: "default",
  ANSWERED: "default",
  CONNECTED: "default",
  ON_HOLD: "outline",
  COMPLETED: "secondary",
  FAILED: "destructive",
  NO_ANSWER: "outline",
  BUSY: "outline",
  SKIPPED: "outline",
  VOICEMAIL: "outline",
  ABANDONED: "destructive",
  CANCELED: "outline",
  SUPPRESSED: "outline",
};

const enrollmentVariants: Record<string, "default" | "secondary" | "outline" | "destructive"> = {
  ENROLLED: "default",
  AWAITING_CONSENT: "outline",
  CONTACTED: "default",
  REPLIED: "secondary",
  CONVERTED: "default",
  SUPPRESSED: "destructive",
  DONE: "secondary",
};

const removableEnrollmentStatuses = [null, undefined, "ENROLLED", "AWAITING_CONSENT", "CONTACTED"];

export default function CampaignContactsSection({
  campaignId,
  contacts,
  isLoading,
  campaignType,
}: CampaignContactsSectionProps) {
  const isSequence = campaignType === "SEQUENCE";
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<{ contactId: string; name: string } | null>(null);
  const [page, setPage] = useState(0);
  const { data: enrollments, isLoading: enrollmentsLoading } = useGetCampaignEnrollmentsQuery(
    { campaignId, page, size: 50 },
    { skip: !isSequence },
  );
  const [removeCampaignContacts, { isLoading: isRemoving }] = useRemoveCampaignContactsMutation();

  const contactCount = isSequence ? (enrollments?.totalElements ?? 0) : contacts.length;

  const handleRemove = async () => {
    if (!removeTarget) {
      return;
    }
    try {
      await removeCampaignContacts({ campaignId, contactIds: [removeTarget.contactId] }).unwrap();
      toast.success(`${removeTarget.name} removed from campaign`);
    } catch (error) {
      toast.error(
        (error as { data?: { message?: string } })?.data?.message || "Failed to remove contact",
      );
    } finally {
      setRemoveTarget(null);
    }
  };

  const renderEnrollmentRow = (enrollment: SequenceEnrollment) => {
    const contactId = enrollment.contact?.id;
    const contactName = [enrollment.contact?.firstName, enrollment.contact?.lastName].filter(Boolean).join(" ");
    const name = contactName || enrollment.phoneNumber || "Unknown";
    const removable = removableEnrollmentStatuses.includes(enrollment.outreachStatus);
    return (
      <TableRow key={enrollment.id}>
        <TableCell className="text-muted-foreground text-sm">{enrollment.sequence}</TableCell>
        <TableCell className="font-medium text-sm">{name}</TableCell>
        <TableCell>
          <span className="text-sm text-muted-foreground">{enrollment.phoneNumber ?? "—"}</span>
        </TableCell>
        <TableCell>
          {enrollment.outreachStatus ? (
            <Badge variant={enrollmentVariants[enrollment.outreachStatus] ?? "outline"}>
              {enrollment.outreachStatus.replace(/_/g, " ")}
            </Badge>
          ) : (
            <Badge variant="secondary">QUEUED</Badge>
          )}
        </TableCell>
        <TableCell>
          {enrollment.currentStepActionType ? (
            <div className="text-sm">
              <span>{enrollment.currentStepActionType.replace(/_/g, " ")}</span>
              {enrollment.currentStepScheduledAt && (
                <span className="text-xs text-muted-foreground block">
                  {format(new Date(enrollment.currentStepScheduledAt), "MMM dd, HH:mm")}
                </span>
              )}
            </div>
          ) : (
            <span className="text-sm text-muted-foreground">{enrollment.exitReason ?? "—"}</span>
          )}
        </TableCell>
        <TableCell className="text-right">
          {removable && contactId && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={isRemoving}
              onClick={() => setRemoveTarget({ contactId, name })}
              className="text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </TableCell>
      </TableRow>
    );
  };

  const showLoading = isSequence ? enrollmentsLoading : isLoading;
  const isEmpty = isSequence ? (enrollments?.items?.length ?? 0) === 0 : contacts.length === 0;

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Contacts</CardTitle>
              <CardDescription>
                {contactCount} {contactCount === 1 ? "contact" : "contacts"} in this campaign
              </CardDescription>
            </div>
            <Button type="button" size="sm" onClick={() => setAddDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Contacts
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {showLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : isEmpty ? (
            <div className="flex flex-col items-center justify-center py-8">
              <Users className="h-10 w-10 text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground mb-3">No contacts added yet</p>
              <Button type="button" size="sm" onClick={() => setAddDialogOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Add Contacts
              </Button>
            </div>
          ) : isSequence ? (
            <div className="space-y-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>#</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Phone Number</TableHead>
                    <TableHead>Enrollment</TableHead>
                    <TableHead>Next Step</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>{enrollments?.items?.map(renderEnrollmentRow)}</TableBody>
              </Table>
              {enrollments && enrollments.totalPages > 1 && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    Page {enrollments.page + 1} of {enrollments.totalPages}
                  </span>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>
                      Previous
                    </Button>
                    <Button type="button" variant="outline" size="sm" disabled={enrollments.last} onClick={() => setPage(page + 1)}>
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Phone Number</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Disposition</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contacts.map((campaignContact) => (
                  <TableRow key={campaignContact.id}>
                    <TableCell className="text-muted-foreground text-sm">{campaignContact.sequence}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8">
                          {campaignContact.contact?.photo && (
                            <AvatarImage src={campaignContact.contact.photo} alt={getContactDisplayName(campaignContact.contact) ?? undefined} />
                          )}
                          <AvatarFallback className="text-xs">
                            {(() => {
                              if (campaignContact.contact) {
                                const initials = getInitials(campaignContact.contact.firstName, campaignContact.contact.lastName);
                                if (initials !== "?") return initials;
                              }
                              return <User className="h-4 w-4" />;
                            })()}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-medium text-sm">
                            {getContactDisplayName(campaignContact.contact) ?? campaignContact.phoneNumber ?? "Unknown"}
                          </div>
                          {campaignContact.contact?.company && (
                            <div className="text-xs text-muted-foreground">{campaignContact.contact.company}</div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">{campaignContact.phoneNumber ?? "—"}</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant={statusVariants[campaignContact.status]}>{campaignContact.status}</Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">{campaignContact.disposition ?? "—"}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      {campaignContact.status === "QUEUED" && campaignContact.contact?.id && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={isRemoving}
                          onClick={() =>
                            setRemoveTarget({
                              contactId: campaignContact.contact!.id,
                              name: getContactDisplayName(campaignContact.contact) ?? "this contact",
                            })
                          }
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <AddContactsDialog campaignId={campaignId} open={addDialogOpen} onOpenChange={setAddDialogOpen} />

      <AlertDialog open={!!removeTarget} onOpenChange={() => setRemoveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Contact</AlertDialogTitle>
            <AlertDialogDescription>
              Remove <span className="font-semibold">{removeTarget?.name}</span> from this campaign?
              {isSequence && " Their remaining sequence steps will not run."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemove} className="bg-destructive">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
